import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, optionalAvathar, AnomalyRepStatus, defaultAllSelection } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Groups2Rounded from "@mui/icons-material/Groups2Rounded";
import ReportProblemRounded from "@mui/icons-material/ReportProblemRounded";
import WatchLaterRounded from "@mui/icons-material/WatchLaterRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import { attendanceAnomilesHeader } from "../TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const sumHhMmValues = (values) => {
  const totalMinutes = (values || []).reduce((sum, value) => {
    const match = /^(\d{1,4}):(\d{2})$/.exec(`${value ?? ""}`.trim());
    if (!match) return sum;
    return sum + Number(match[1]) * 60 + Number(match[2]);
  }, 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const formatAnomalyDate = (dateValue) => {
  const parsedDate = moment(dateValue, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
  return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : dateValue;
};

const normalizeAnomalyRows = (rows = []) =>
  rows.flatMap((row) => {
    if (!row?.dates || typeof row.dates !== "object") {
      return [row];
    }

    return Object.entries(row.dates).map(([date, dayData]) => ({
      ...dayData,
      id: `${row?.user_id ?? row?.employee_id}-${date}`,
      date: formatAnomalyDate(date),
      username: row?.username,
      user_id: row?.user_id,
      employee_id: row?.employee_id,
      image_url: row?.image_url,
    }));
  });

export const AnomaliesReport = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
}) => {
  const {
    domain,
    token,
    userDetails,
    topbarCompanyId,
    userScreenAccess,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anomalyRepDetails, setAnomalyRepDetails] = useState([]);
  const [anomilesData, setAnomilesData] = useState([]);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "yesterday" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [anoSts, setAnoSts] = useState("All");
  const [anoStatusValue, setAnoStatusValue] = useState("");
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  // Real payloads return "Attendance Anomalies" (no "Report" suffix) for
  // this screen's sub_module_name — subModuleNameMatches applies the same
  // alias table checkViewerPermission below relies on, instead of a raw
  // === that would silently land on undefined and send an invalid
  // submodule id when saving a view.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Attendance Anomalies Report"),
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Attendance Anomalies Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "attendance_anomalies_report_saved_views",
    fullHeaders: attendanceAnomilesHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  // shift_start_time/shift_end_time render as one combined "Shift Time"
  // column on screen, so they're merged back into a single virtual column
  // (at whichever position the first of the pair appears in the saved
  // order) rather than following visibleHeaders 1:1 like every other
  // column here.
  const shiftTimeVisibility = useMemo(
    () => ({
      hasStart: visibleHeaders.some((header) => header.key === "shift_start_time"),
      hasEnd: visibleHeaders.some((header) => header.key === "shift_end_time"),
    }),
    [visibleHeaders],
  );

  const anomalyColumns = useMemo(() => {
    let shiftTimeInserted = false;
    return visibleHeaders.reduce((columns, header) => {
      if (header.key === "shift_start_time" || header.key === "shift_end_time") {
        if (shiftTimeInserted) return columns;
        shiftTimeInserted = true;
        const { hasStart, hasEnd } = shiftTimeVisibility;
        columns.push({
          key: "shift_time",
          name: hasStart && hasEnd ? "Shift Time" : header.name,
        });
        return columns;
      }
      columns.push(header);
      return columns;
    }, []);
  }, [visibleHeaders, shiftTimeVisibility]);

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams({
          user_id: userDetails.id,
          start_date: startDate,
          end_date: endDate,
          department_id: departmentId,
          company_id: topbarCompanyId,
        });

        if (anoStatusValue) {
          queryParams.set("status", anoStatusValue);
        }

        const response = await fetch(
          `${domain}anomalies-report/?${queryParams.toString()}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const rows = normalizeAnomalyRows(res?.data ?? []);
          setAnomalyRepDetails(rows);
          setAnomilesData(rows);
        } else if (response.status === 409) {
          setAnomalyRepDetails([]);
          setAnomilesData([]);
          sessionHandling();
        } else {
          setAnomalyRepDetails([]);
          setAnomilesData([]);
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Anomalies] operation failed:', error);
        setAnomalyRepDetails([]);
        setAnomilesData([]);
        showToast("error", "Couldn't load the anomaly report. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [
    domain,
    token,
    startDate,
    endDate,
    anoStatusValue,
    departmentId,
    userDetails.id,
    topbarCompanyId,
  ]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStartDate = moment(rangeStart);
    const parsedEndDate = moment(rangeEnd);

    if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
      return;
    }

    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setEndDate(parsedEndDate.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const departmentList = useMemo(
    () => [
      {
        id: defaultAllSelection,
        label: "All",
        department_name: "All",
      },
      ...(departmentData?.map((obj) => ({
        ...obj,
        label: obj.department_name,
      })) ?? []),
    ],
    [departmentData],
  );

  const updateDepartmentName = (event, data) => {
    if (data && data?.label) {
      setDepartmentName(data.label);
      setDepartmentId(data.id);
      setPage(0);
    }
  };

  const updateStatus = (event, data) => {
    if (!data) {
      setAnoSts("All");
      setAnoStatusValue("");
      return;
    }

    setAnoSts(data.label);
    setAnoStatusValue(data.value ?? (data.label === "All" ? "" : data.label));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setAnomilesData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    const rows = anomilesData || [];
    const uniqueEmployeeCount = new Set(
      rows.map((row) => row?.user_id || row?.employee_id).filter(Boolean),
    ).size;
    const countByStatus = (status) =>
      rows.filter((row) => row?.status === status).length;

    return {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      "Status Type": anoSts || "N/A",
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      Employees: uniqueEmployeeCount,
      "Total Anomalies": rows.length,
      Missing: countByStatus("Missing"),
      Late: countByStatus("Late"),
      Absent: countByStatus("Absent"),
    };
  }, [anoSts, anomilesData, departmentName, endDate, startDate, topbarCompanyName]);

  const handleAnomaliesDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const summaryRow = {
        rowType: "grand_total",
        username: "TOTAL",
        late_hours: sumHhMmValues(rows.map((row) => row?.late_hours)),
      };

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Attendance Anomalies",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [downloadCsv, downloadExcel, downloadPdf, getReportMetadata, visibleHeaders],
  );

  // Same StatCard row every other report in this migration now shows,
  // derived from the currently visible (searched/filtered) rows.
  const anomalySummaryCards = useMemo(() => {
    const rows = anomilesData || [];
    const uniqueEmployeeCount = new Set(
      rows.map((row) => row?.user_id || row?.employee_id).filter(Boolean),
    ).size;
    const countByStatus = (status) =>
      rows.filter((row) => row?.status === status).length;

    return [
      {
        key: "employees",
        title: "Employees",
        value: uniqueEmployeeCount,
        icon: Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "total",
        title: "Total Anomalies",
        value: rows.length,
        icon: ReportProblemRounded,
        accent: "#D97706",
        tint: "#FFF6EA",
      },
      {
        key: "missing",
        title: "Missing",
        value: countByStatus("Missing"),
        icon: HelpOutlineRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "late",
        title: "Late",
        value: countByStatus("Late"),
        icon: WatchLaterRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
      {
        key: "absent",
        title: "Absent",
        value: countByStatus("Absent"),
        icon: EventBusyRounded,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
    ];
  }, [anomilesData]);

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const headerFilterControls = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={false}
          includeCurrentDate={false}
          allowFutureDate={false}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentList}
          selectedValue={departmentName}
          updateDataFunction={updateDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={AnomalyRepStatus}
          selectedValue={anoSts}
          updateDataFunction={updateStatus}
          Icons={PersonOutlineIcon}
          staticLabel="Status Type"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 200 },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      dateRangeLabel,
      departmentList,
      departmentName,
      anoSts,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  // Filters render inline in the page body (matching Employee Attendance's
  // own row below the title), not floated beside the title.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const renderAnomalyCell = (key, item) => {
    switch (key) {
      case "username":
        return (
          <Mui.Tooltip
            arrow
            slotProps={tooltipStyleProps}
            title={item?.username}
            disableHoverListener={!item?.username}
          >
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              <Mui.Avatar
                sx={{ width: 28, height: 28 }}
                alt="Remy Sharp"
                src={item?.image_url ?? optionalAvathar}
              />
              <Mui.Typography
                sx={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  color: designColors.slate900,
                  fontSize: 14,
                }}
              >
                {item?.username}
              </Mui.Typography>
            </Mui.Stack>
          </Mui.Tooltip>
        );
      case "employee_id":
        return item?.employee_id ? item?.employee_id : "N/A";
      case "date":
        return item?.date ? item?.date : "N/A";
      case "shift_name":
        return (
          <Mui.Tooltip
            arrow
            slotProps={tooltipStyleProps}
            title={item?.shift_name}
            disableHoverListener={!item?.shift_name}
          >
            <Mui.Typography
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {item?.shift_name ? item?.shift_name : "Unassigned"}
            </Mui.Typography>
          </Mui.Tooltip>
        );
      case "shift_time": {
        const { hasStart, hasEnd } = shiftTimeVisibility;
        const start = item?.shift_start_time ? item?.shift_start_time : "N/A";
        const end = item?.shift_end_time ? item?.shift_end_time : "N/A";
        if (hasStart && hasEnd) return `${start} - ${end}`;
        return hasStart ? start : end;
      }
      case "check_in_time":
        return item?.check_in_time ? item?.check_in_time : "N/A";
      case "check_out_time":
        return item?.check_out_time ? item?.check_out_time : "N/A";
      case "status":
        return item?.status ? item?.status : "N/A";
      case "late_hours":
        return item?.late_hours ? item?.late_hours : "N/A";
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {/* Filters sit in their own row directly under the page title —
              same placement as Employee Attendance Report — instead of
              floating beside the title via setReportHeaderActions. */}
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            {headerFilterControls}
          </Mui.Box>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {anomalySummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Attendance Anomalies"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={anomalyRepDetails}
              filteredData={anomilesData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              customDownloadHandler={handleAnomaliesDownload}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.Box>
                <Mui.TableContainer
                  sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: { xs: "auto", sm: "visible" },
                    overflowY: { xs: "auto", sm: "visible" },
                    pb: footerHeight ? `${footerHeight}px` : 0,
                    borderLeft: `1px solid ${designColors.slate200}`,
                    borderRight: `1px solid ${designColors.slate200}`,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <ApproveTable stickyHeader sx={{ minWidth: 900, width: "100%" }}>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {anomalyColumns.map((column, columnIndex) => (
                          <Mui.TableCell
                            key={column.key}
                            sx={{
                              ...approveTableHeadCell,
                              top: APP_BAR_OFFSET + customFilterHeight,
                              ...(columnIndex === 0
                                ? { borderTopLeftRadius: 0 }
                                : {}),
                              ...(columnIndex === anomalyColumns.length - 1
                                ? { borderTopRightRadius: 0 }
                                : {}),
                            }}
                          >
                            {column.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {anomilesData?.length === 0 ? (
                        <TableEmptyState dataLength={anomalyRepDetails} />
                      ) : (
                        anomilesData
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((item, index) => (
                            <Mui.TableRow
                              key={
                                item?.id ??
                                `${item?.user_id ?? item?.employee_id}-${item?.date}-${index}`
                              }
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              {anomalyColumns.map((column) => (
                                <Mui.TableCell
                                  key={column.key}
                                  sx={approveTableBodyCell}
                                >
                                  {renderAnomalyCell(column.key, item)}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
              </Mui.Box>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: anomilesData?.length || 0,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
